// src/pages/agents/AgentSettingsTab.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, Paper, Alert,
} from '@mui/material';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { Save as SaveIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import AgentForm from '../../components/agents/AgentForm'; // Correct path
import { agentService } from '../../api/agentService';

const AgentSettingsTab = () => {
  const { agent: initialAgentContextData } = useOutletContext(); // Renamed to avoid conflict
  const { agentId } = useParams();
  const navigate = useNavigate(); // Not used for navigation, but kept if needed
  const formRef = useRef(); // To trigger form submission

  const [currentAgentDataForForm, setCurrentAgentDataForForm] = useState(null);
  const [formKey, setFormKey] = useState(Date.now()); // Used to force re-render AgentForm on reset

  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Function to prepare data for the form (including fetching tool slugs)
  const prepareInitialFormData = useCallback(async () => {
    if (initialAgentContextData && initialAgentContextData.id) {
      setIsSaving(true); // Show loading indicator while preparing
      const toolsRes = await agentService.getAgentTools(initialAgentContextData.id);
      const currentToolSlugs = toolsRes.isError ? [] : (Array.isArray(toolsRes) ? toolsRes : []);
      
      setCurrentAgentDataForForm({
        name: initialAgentContextData.name,
        description: initialAgentContextData.description,
        llm_model: initialAgentContextData.llm_model,
        is_public: initialAgentContextData.is_public,
        agent_settings: initialAgentContextData.agent_settings, // Pass the object as is
        tool_slugs: currentToolSlugs,
      });
      setFormKey(Date.now()); // Change key to force re-initialization of AgentForm
      setIsSaving(false);
    } else {
      setCurrentAgentDataForForm(null); // No data to show
    }
  }, [initialAgentContextData]);

  useEffect(() => {
    prepareInitialFormData();
  }, [prepareInitialFormData]);


  const handleUpdateAgent = async (formData) => {
    if (!agentId) return;
    setIsSaving(true);
    setError(null);

    const res = await agentService.updateAgent(agentId, formData);
    setIsSaving(false);

    if (res.isError) {
      setError(res.message || 'Failed to update agent.');
      toast.error(res.message || 'Failed to update agent.');
    } else {
      toast.success('Agent settings updated successfully.');
      // Potentially trigger a refetch in AgentDetailLayout or simply rely on user seeing changes
      // For a full refresh effect to see changes everywhere:
      // window.location.reload();
      // Or, ideally, AgentDetailLayout would have a refresh function passed via context
      // For now, we'll just prepareInitialFormData again to update the form if needed.
      // This relies on initialAgentContextData being updated by AgentDetailLayout if it refetches.
      // A better solution would be a state management that triggers refetch or updates context.
      // For simplicity here, we call prepare again. If outlet context isn't updated, this won't show deep backend changes.
      prepareInitialFormData(); // Re-prepare form data, might reflect some saved changes if backend returns full object
    }
  };

  const handleReset = () => {
    setError(null);
    if (initialAgentContextData) {
        toast.info("Resetting form to last saved state...");
        prepareInitialFormData(); // This will refetch tool_slugs and re-key the form
    }
  };

  const triggerFormSubmit = () => {
     if (formRef.current) {
        const formElement = formRef.current.querySelector('form'); 
        if (formElement) {
            formElement.requestSubmit();
        } else {
            console.error("Could not find form element to submit in AgentForm (SettingsTab).");
        }
    }
  };

  if (!currentAgentDataForForm && !initialAgentContextData) { // Agent context not loaded yet
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ml: 2}}>Loading agent data for settings...</Typography>
      </Box>
    );
  }
  if (!currentAgentDataForForm && initialAgentContextData) { // Agent context loaded, but preparing form data
     return (
      <Box display="flex" justifyContent="center" alignItems="center" p={3}>
        <CircularProgress />
        <Typography sx={{ml: 2}}>Preparing settings form...</Typography>
      </Box>
    );
  }
  if (!initialAgentContextData) { // Should not happen if layout works, but as a guard
     return <Alert severity="error">Agent data not available for settings tab.</Alert>;
  }


  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, maxWidth: 800 }}>
      <Typography variant="h6" mb={2}>
        Agent Core Settings
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      
      <Box ref={formRef}>
        <AgentForm
            key={formKey} // Force re-mount and re-initialization on reset
            initialData={currentAgentDataForForm} // Pass the prepared data
            isEditing={true}
            onSubmit={handleUpdateAgent}
            isSaving={isSaving}
        />
      </Box>

      <Box mt={3} display="flex" justifyContent="flex-end" gap={2}>
        <Button
          onClick={handleReset}
          disabled={isSaving}
          startIcon={<RefreshIcon />}
        >
          Reset Changes
        </Button>
        <Button
          variant="contained"
          onClick={triggerFormSubmit}
          disabled={isSaving} // Add other disabling conditions if form is loading its own data
          startIcon={isSaving ? <CircularProgress size={20} color="inherit"/> : <SaveIcon />}
        >
          Save Changes
        </Button>
      </Box>
    </Paper>
  );
};

export default AgentSettingsTab;